/**
 * StudentSamSessionScreen — la route `sam-session`, hors live : l'activité, Sam en plus (sa tuile, sa
 * conversation), sur la même page que `activity`.
 *
 * Ce que l'ancien `SamSessionScreen` garantissait est gardé : l'id de la route lu en nombre (un lien le
 * donne en chaîne) ; l'activité retrouvée dans la todo et retenue (rendue, elle quitte la todo) ; un
 * contexte de Sam en échec ramène l'activité sans Sam (`activity`) ; l'enchaînement « avec Sam »
 * prend la suite de la séquence quand elle existe, sinon la plus récente de la todo.
 */
import { useNavigation } from "@react-navigation/native"
import React, { useEffect, useMemo, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import { PageTemplate, Skeleton, SkeletonGroup, Stack, Text } from "../../../../../../design-system"
import { setCurrentActivity } from "../../../../../../data/redux/actions/activity"
import { type ApiRunActivity } from "../../../../adapters/student/Activity/studentActivityAdapter"
import { type StudentSpaceScreenProps } from "../studentSpaceRouteTypes"
import { StudentActivityBody, type StudentActivityOutcome } from "./StudentActivityBody"
import { useStudentActivityExit } from "./useStudentActivityExit"
import { useStudentActivityView } from "./useStudentActivityView"
import { useStudentSamLayer } from "./useStudentSamLayer"
import { selectDisplayedStudent } from "../../../../view-models/shared/displayedStudent"

type SamSessionRoot = {
    data?: {
        currentUser?: { object?: { todo_activities?: ApiRunActivity[] } | null }
        sam?: { contextByActivityId?: Record<string, { sequence?: { next_activity_id?: number | null } | null }> }
    }
}

const SAM_WAIT_HINT = "On prépare ta séance avec Sam…"
const SAM_WAIT_HINT_MS = 1500

export function StudentSamSessionScreen({ route }: StudentSpaceScreenProps) {
    const navigation = useNavigation() as unknown as {
        push: (route: string, params?: object) => void
        replace: (route: string, params?: object) => void
    }
    const dispatch = useDispatch()
    const activityId = Number(route?.params?.activityId) || null
    const todo = useSelector((state: SamSessionRoot) => selectDisplayedStudent(state)?.todo_activities)

    // Retenue une fois trouvée : rendue, l'activité quitte la todo, la correction doit rester.
    const found = useMemo(() => todo?.find((activity) => activity?.id === activityId) ?? null, [todo, activityId])
    const kept = useRef<ApiRunActivity | null>(null)
    if (found != null) kept.current = found
    if (kept.current != null && kept.current.id !== activityId) kept.current = found
    const activity = found ?? kept.current

    // La suite de la séquence, quand le contexte de Sam en a une (l'intention du tuteur).
    const sequenceNext = useSelector(
        (state: SamSessionRoot) =>
            (activityId != null
                ? state.data?.sam?.contextByActivityId?.[activityId]?.sequence?.next_activity_id
                : null) ?? null
    )
    const view = useStudentActivityView({
        activity,
        withSam: true,
        nextActivityId: sequenceNext,
        onAddMaterial: (id) => navigation.push("new-material", { activityId: id }),
    })
    const submitted = view.run.state.phase !== "working"
    const entryPoint = typeof route?.params?.entry_point === "string" ? route.params.entry_point : null
    const sam = useStudentSamLayer(activityId, submitted, entryPoint)
    const exit = useStudentActivityExit(view.nextActivity)

    // Le contexte de Sam en échec : l'activité reste jouable, sans Sam (`launchActivity(…, "replace")`).
    useEffect(() => {
        if (!sam.contextError || activity == null) return
        dispatch(setCurrentActivity({ activityData: activity, progressStatus: "ongoing" }) as never)
        navigation.replace("activity")
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sam.contextError])

    // L'attente se dit au bout d'1,5 s (`SamSessionScreen`) : sur un chargement rapide, le mot ne
    // ferait que clignoter ; sans lui, la forme muette laisse croire que rien ne vient.
    const waiting = activity == null || sam.context == null
    const [waitHint, setWaitHint] = useState(false)
    useEffect(() => {
        setWaitHint(false)
        if (!waiting) return undefined
        const timer = setTimeout(() => setWaitHint(true), SAM_WAIT_HINT_MS)
        return () => clearTimeout(timer)
    }, [waiting])

    const onFinished = (outcome: StudentActivityOutcome) => {
        sam.reset()
        exit(outcome)
    }

    if (waiting) {
        return (
            <PageTemplate
                loading
                skeleton={
                    <Stack gap="4">
                        <SkeletonGroup>
                            <Skeleton textSize="text-lg" width="60%" />
                            <Skeleton height={240} />
                            <Skeleton textSize="text-md" width="80%" />
                        </SkeletonGroup>
                        {waitHint && (
                            <Text color="text-muted" center>
                                {SAM_WAIT_HINT}
                            </Text>
                        )}
                    </Stack>
                }
            >
                {null}
            </PageTemplate>
        )
    }

    return <StudentActivityBody view={view} onFinished={onFinished} samTile={sam.samTile} samLayer={sam.samLayer} />
}
